import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { usePaymentSettings } from '../../../hooks/usePaymentSettings';

export default function CartSidebar() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalItems, totalPrice } = useCart();
  const { settings } = usePaymentSettings();

  const freeShippingLimit = settings.ucretsiz_kargo_limiti;
  const shippingBase = settings.kargo_ucreti;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const shippingCost = totalPrice >= freeShippingLimit ? 0 : shippingBase;
  const remaining = freeShippingLimit - totalPrice;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeCart}
      />

      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-cream-dark">
          <div className="flex items-center gap-3">
            <h2 className="font-serif text-xl font-semibold text-coffee">Sepetim</h2>
            {totalItems > 0 && (
              <span className="w-6 h-6 flex items-center justify-center bg-gold text-white text-xs font-bold rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-cream transition-colors text-coffee-light"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Free Shipping Progress */}
        {totalPrice > 0 && totalPrice < freeShippingLimit && (
          <div className="px-6 py-3 bg-cream">
            <p className="text-xs text-coffee-medium mb-2">
              <strong>{remaining.toFixed(0)}₺</strong> daha ekle, ücretsiz kargo kazan!
            </p>
            <div className="w-full h-1.5 bg-cream-dark rounded-full overflow-hidden">
              <div
                className="h-full bg-gold rounded-full transition-all duration-500"
                style={{ width: `${Math.min((totalPrice / freeShippingLimit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
        {totalPrice >= freeShippingLimit && totalPrice > 0 && (
          <div className="px-6 py-3 bg-green-50 flex items-center gap-2">
            <i className="ri-truck-line text-green-600 text-sm" />
            <p className="text-xs text-green-700 font-medium">Ücretsiz kargo kazandın!</p>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-cream rounded-full mb-4">
                <i className="ri-shopping-bag-line text-2xl text-coffee-light" />
              </div>
              <p className="font-medium text-coffee mb-1">Sepetiniz boş</p>
              <p className="text-sm text-coffee-light">Ürün eklemek için alışverişe başlayın</p>
            </div>
          ) : (
            items.map(item => (
              <div key={item.id} className="flex gap-4">
                <Link to={`/urun/${item.slug}`} onClick={closeCart} className="flex-shrink-0">
                  <div className="w-20 h-20 bg-cream rounded-xl overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/urun/${item.slug}`} onClick={closeCart}>
                    <p className="text-sm font-medium text-coffee leading-snug mb-1 truncate hover:text-gold transition-colors">
                      {item.name}
                    </p>
                  </Link>
                  {(item.variant_name || item.weight) && (
                    <p className="text-xs text-coffee-light mb-2">
                      {item.variant_name ? item.variant_name : item.weight}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-cream-dark rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variant_id)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-cream transition-colors text-coffee-light hover:text-coffee"
                      >
                        <i className="ri-subtract-line text-sm" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium text-coffee">{item.quantity}</span>
                      <button
                        onClick={() => {
                          // Stok kontrolü: stock number ve > 0 olmalı
                          const currentStock = typeof item.stock === 'number' ? item.stock : 0;
                          if (item.quantity < currentStock) {
                            updateQuantity(item.product_id, item.quantity + 1, item.variant_id);
                          }
                        }}
                        disabled={!(typeof item.stock === 'number' && item.stock > 0 && item.quantity < item.stock)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-cream transition-colors text-coffee-light hover:text-coffee disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <i className="ri-add-line text-sm" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-coffee">
                        {(item.price * item.quantity).toLocaleString('tr-TR')}₺
                      </span>
                      <button
                        onClick={() => removeItem(item.product_id, item.variant_id)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-red-50 text-coffee-light hover:text-red-500 rounded-lg transition-colors"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-cream-dark space-y-3">
            <div className="flex justify-between text-sm text-coffee-medium">
              <span>Ara Toplam</span>
              <span>{totalPrice.toLocaleString('tr-TR')}₺</span>
            </div>
            <div className="flex justify-between text-sm text-coffee-medium">
              <span>Kargo</span>
              <span>{shippingCost === 0 ? <span className="text-green-600">Ücretsiz</span> : `${shippingCost}₺`}</span>
            </div>
            <div className="flex justify-between text-base font-semibold text-coffee pt-2 border-t border-cream-dark">
              <span>Toplam</span>
              <span>{(totalPrice + shippingCost).toLocaleString('tr-TR')}₺</span>
            </div>
            <Link
              to="/odeme"
              onClick={closeCart}
              className="block w-full py-3.5 bg-coffee text-cream text-center font-medium rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap"
            >
              Ödemeye Geç
            </Link>
            <button
              onClick={closeCart}
              className="block w-full py-3 text-center text-sm text-coffee-light hover:text-coffee transition-colors whitespace-nowrap"
            >
              Alışverişe Devam Et
            </button>
          </div>
        )}
      </div>
    </>
  );
}
